import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { get } from '@aws-amplify/api';
import * as Auth from 'aws-amplify/auth';
import {
  TrendingUp,
  TrendingDown,
  RefreshCw,
  ChevronDown,
} from "lucide-react";
import AppLayout from './components/AppLayout';
import './LandingPage.css';

/**
 * Dashboard Landing Page matching landingpageimage.png
 */
export default function LandingPage({ signOut }) {
  const navigate = useNavigate();
  const [activeNewsTab, setActiveNewsTab] = useState("Business");
  const [activeMarketTab, setActiveMarketTab] = useState("gainers");
  const [indices, setIndices] = useState([
    { symbol: "SPX", name: "S&P 500", value: "...", change: "...", pct: "...", trend: "up", isFuture: false },
    { symbol: "DJIA", name: "Dow Jones Industrial", value: "...", change: "...", pct: "...", trend: "up", isFuture: false },
    { symbol: "IXIC", name: "NASDAQ Composite", value: "...", change: "...", pct: "...", trend: "up", isFuture: false },
    { symbol: "NDX", name: "NASDAQ 100", value: "...", change: "...", pct: "...", trend: "up", isFuture: false },
    { symbol: "RUT", name: "Russell 2000", value: "...", change: "...", pct: "...", trend: "up", isFuture: false },
    { symbol: "RUI", name: "Russell 1000", value: "...", change: "...", pct: "...", trend: "up", isFuture: false },
    { symbol: "VIX", name: "CBOE Volatility", value: "...", change: "...", pct: "...", trend: "up", isFuture: false },
  ]);
  const [marketTimestamp, setMarketTimestamp] = useState("");
  const [topGainers, setTopGainers] = useState([]);
  const [topLosers, setTopLosers] = useState([]);
  const [newsData, setNewsData] = useState([]);
  const [newsUpdatedTime, setNewsUpdatedTime] = useState("");

  const [economicEvents, setEconomicEvents] = useState([]);
  const [eventsUpdatedTime, setEventsUpdatedTime] = useState("");

  const [moversUpdatedTime, setMoversUpdatedTime] = useState("");
  const [loadingMovers, setLoadingMovers] = useState(false);

  const [sectorData, setSectorData] = useState([]);
  const [sectorUpdatedTime, setSectorUpdatedTime] = useState("");

  const [marketImpactData, setMarketImpactData] = useState([]);
  const [marketImpactUpdatedTime, setMarketImpactUpdatedTime] = useState("");

  const [volatilityData, setVolatilityData] = useState([
    { security: "VIX1D", shortName: "1-Day Volatility", lastSale: "...", change: "...", changePercent: "..." },
    { security: "VIX9D", shortName: "9-Day Volatility", lastSale: "...", change: "...", changePercent: "..." },
    { security: "VIX", shortName: "CBOE Volatility", lastSale: "...", change: "...", changePercent: "..." },
    { security: "VIX3M", shortName: "3-Month Volatility", lastSale: "...", change: "...", changePercent: "..." },
    { security: "VIX6M", shortName: "6-Month Volatility", lastSale: "...", change: "...", changePercent: "..." },
    { security: "VIX1Y", shortName: "1-Year Volatility", lastSale: "...", change: "...", changePercent: "..." },
    { security: "RVX", shortName: "Russell 2000 Volatility", lastSale: "...", change: "...", changePercent: "..." },
    { security: "VXN", shortName: "NASDAQ Volatility", lastSale: "...", change: "...", changePercent: "..." },
  ]);
  const [volatilityUpdatedTime, setVolatilityUpdatedTime] = useState("");

  const [refreshingAll, setRefreshingAll] = useState(false);

  // Quick Actions submenu state
  const [buyWriteExpanded, setBuyWriteExpanded] = useState(false);
  const [ironCondorExpanded, setIronCondorExpanded] = useState(false);
  const [butterflyExpanded, setButterflyExpanded] = useState(false);

  // Track database timestamps for polling
  const [dataTimestamps, setDataTimestamps] = useState({
    marketIndices: null,
    marketMovers: null,
    news: null,
    sectors: null
  });

  // Categorize news by source
  const categorizeNews = (newsArray) => {
    const categories = {
      Business: [],
      Investing: [],
      "TV Networks": [],
      Tech: [],
      Tabloid: [],
      Other: []
    };

    const businessSources = [
      "Reuters", "WSJ", "CNBC", "Bloomberg Markets and Finance",
      "Yahoo Finance", "Barrons", "Market Watch", "Business Insider", "Fast Company"
    ];

    const investingSources = [
      "Seeking Alpha", "Benzinga", "Forbes", "Investopedia", "ETF Trends", "Kitco"
    ];

    const tvNetworkSources = ["CNBC Television", "Schwab Network"];
    const techSources = ["VentureBeat", "PYMNTS"];
    const tabloidSources = ["New York Post"];

    newsArray.forEach(newsItem => {
      const source = newsItem.source || "";

      if (businessSources.includes(source)) {
        categories.Business.push(newsItem);
      } else if (investingSources.includes(source)) {
        categories.Investing.push(newsItem);
      } else if (tvNetworkSources.includes(source)) {
        categories["TV Networks"].push(newsItem);
      } else if (techSources.includes(source)) {
        categories.Tech.push(newsItem);
      } else if (tabloidSources.includes(source)) {
        categories.Tabloid.push(newsItem);
      } else {
        categories.Other.push(newsItem);
      }
    });

    return categories;
  };

  // Get color based on percentage value
  const getSectorColor = (percentChange) => {
    const pct = parseFloat(percentChange);
    if (pct >= 2) return "#059669";        // Dark green >= 2%
    if (pct >= 1) return "#10b981";        // Green 1-1.99%
    if (pct >= 0) return "#34d399";        // Light green 0-0.99%
    if (pct >= -1) return "#fca5a5";       // Light red 0-(-0.99)%
    if (pct >= -2) return "#f87171";       // Red (-1) - (-1.99)%
    return "#dc2626";                       // Dark red >= (-2)%
  };

  const watchlist = [
    { symbol: "NVDA", price: "$214.20", change: "+3.35%", trend: "up" },
    { symbol: "TSLA", price: "$245.67", change: "+2.45%", trend: "up" },
    { symbol: "AMD", price: "$142.89", change: "+2.25%", trend: "up" },
    { symbol: "SPY", price: "$690.97", change: "+0.97%", trend: "up" },
  ];

  const quickActions = [
    {
      label: "✍️ Buy Write",
      hasSubmenu: true,
      expanded: buyWriteExpanded,
      setExpanded: setBuyWriteExpanded,
      submenu: [
        { label: "Analysis", route: "/mainMenuBuyWrite" },
        { label: "Portfolio", route: "/myPortfolio" },
        { label: "Settings", route: "/settings" },
      ]
    },
    {
      label: "🦅 Iron Condor",
      hasSubmenu: true,
      expanded: ironCondorExpanded,
      setExpanded: setIronCondorExpanded,
      submenu: [
        { label: "Reverse", route: "/ironCondorMenu" },
        { label: "Traditional", route: "/ironCondorTraditionalMenu" },
        { label: "Reverse Portfolio", route: "/myPortfolioIronCondor", state: { icType: "LONG" } },
        { label: "Traditional Portfolio", route: "/myPortfolioIronCondor", state: { icType: "SHORT" } },
      ]
    },
    {
      label: "🦋 Butterfly",
      hasSubmenu: true,
      expanded: butterflyExpanded,
      setExpanded: setButterflyExpanded,
      submenu: [
        { label: "Long", route: "/butterflyMenu" },
        { label: "Short", route: "#", disabled: true },
        { label: "Iron", route: "#", disabled: true },
        { label: "Portfolio", route: "/myPortfolioButterfly" },
        { label: "Settings", route: "/butterflySettings" },
      ]
    },
    { label: "📈 Long Term", route: "/mainMenuLongTerm" },
    { label: "Market Direction", route: "/marketDirection" },
    { label: "AI Analysis", route: "/aiAnalysis" },
    { label: "Reports", route: "/reports" },
  ];

  // Encode data for API call
  const encodeData = (str) => {
    return encodeURIComponent(str);
  };

  // Function to fetch index data
  const fetchIndexData = async (initialLoad = false) => {
    try {
      const symbolsList = ["SPX", "IXIC", "NDX", "DJIA", "RUT", "RUI", "VIX"];
      const symbolsParam = symbolsList.join(',');

      // Step 1: Fetch existing data from database
      console.log("Fetching existing index data from database...");
      const checkResponse = await get({
        apiName: "mktappapi",
        path: `/getSymbolsDetails/${encodeURIComponent(symbolsParam)}`,
      }).response;
      const currentSymbolsData = await checkResponse.body.json();
      console.log("Current symbols data:", currentSymbolsData);

      let symbolsData = currentSymbolsData;

      // Check if any symbols are marked as futures (indicates market was closed when data was fetched)
      const hasFuturesData = currentSymbolsData.some(s => s.isFuture === true);

      // If this is initial load AND no futures data, just use existing data and return
      // If futures data is present, force an immediate update regardless of initial load
      if (initialLoad && !hasFuturesData) {
        console.log("Initial load - using existing data (no futures)");
      } else {
        if (hasFuturesData) {
          console.log("Futures data detected - forcing immediate update to check market status");
        }
        // Set updated time to now (when refresh was requested)
        const now = new Date();
        const formattedTime = now.toLocaleString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        });
        setMarketTimestamp(formattedTime);

        // Check if SPX data needs to be updated (older than 5 minutes)
        let needsUpdate = true;
        const spxCheck = currentSymbolsData.find(s => s.symbol === "SPX");

        // If futures data is present, force update (already checked above)
        if (hasFuturesData) {
          console.log("Forcing update due to futures data");
          needsUpdate = true;
        } else if (spxCheck && spxCheck.timestamp) {
          let dataTimestamp;
          if (typeof spxCheck.timestamp === 'string') {
            dataTimestamp = new Date(spxCheck.timestamp);
          } else if (typeof spxCheck.timestamp === 'number') {
            dataTimestamp = new Date(spxCheck.timestamp * 1000);
          }

          const now = new Date();
          const timeDifferenceMinutes = (now - dataTimestamp) / (1000 * 60);

          console.log(`SPX data age: ${timeDifferenceMinutes.toFixed(2)} minutes`);

          if (timeDifferenceMinutes <= 5) {
            needsUpdate = false;
            console.log("SPX data is fresh (< 5 minutes old), skipping update");
          } else {
            console.log("SPX data is stale (> 5 minutes old), updating...");
          }
        } else {
          console.log("No SPX timestamp found or no data, will update");
        }

        // Step 2: Update database with latest info if needed
        if (needsUpdate) {
        const securitiesParam = JSON.stringify({ stocks: symbolsList });
        const getSecuritiesInfoPath = `/getSecuritiesInfo/${encodeData(securitiesParam)}`;

        console.log("Updating securities info for indices...");
        console.log("Calling path:", getSecuritiesInfoPath);
        const updateResponse = await get({
          apiName: "mktappapi",
          path: getSecuritiesInfoPath,
        }).response;

        // Wait for the response body to ensure Lambda completes fully
        await updateResponse.body.json();
        console.log("Update completed successfully");

        // Additional delay to ensure database connections are released
        await new Promise(resolve => setTimeout(resolve, 3000));

        // Step 3: Get updated symbols details from database
        console.log("Fetching updated symbols details...");
        const getSymbolsResponse = await get({
          apiName: "mktappapi",
          path: `/getSymbolsDetails/${encodeURIComponent(symbolsParam)}`,
        }).response;

        console.log("getSymbolsResponse status:", getSymbolsResponse.statusCode);
          symbolsData = await getSymbolsResponse.body.json();
          console.log("Updated symbols data received:", symbolsData);
        }
      }

      console.log("Symbols data type:", typeof symbolsData);
      console.log("Symbols data length:", symbolsData?.length);
      if (symbolsData && symbolsData.length > 0) {
        console.log("First symbol data:", symbolsData[0]);
        console.log("All symbols in database:", symbolsData.map(s => s.symbol));
      }
      console.log("Current indices state:", indices);

      // Step 4: Update indices state with real data
      const updatedIndices = indices.map(index => {
        console.log("Looking for symbol:", index.symbol);
        const symbolData = symbolsData.find(s => {
          console.log("Comparing with DB symbol:", s.symbol);
          return s.symbol === index.symbol;
        });

        if (symbolData) {
          console.log("Found data for", index.symbol, ":", symbolData);
          const currentPrice = parseFloat(symbolData.regularMarketPrice || 0);
          const dollarChange = parseFloat(symbolData.regularMarketChange || 0);
          const percentChange = parseFloat(symbolData.regularMarketChangePercent || 0);
          const isFuture = symbolData.isFuture || false;

          console.log(`${index.symbol} - Price: ${currentPrice}, Change: ${dollarChange}, Percent: ${percentChange}, isFuture: ${isFuture}`);

          return {
            ...index,
            value: currentPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            change: (dollarChange >= 0 ? '+' : '') + dollarChange.toFixed(2),
            pct: (percentChange >= 0 ? '+' : '') + percentChange.toFixed(2) + '%',
            trend: dollarChange >= 0 ? 'up' : 'down',
            isFuture: isFuture
          };
        } else {
          console.log("No data found for symbol:", index.symbol);
        }
        return index;
      });

      console.log("Updated indices:", updatedIndices);
      setIndices(updatedIndices);

      // Save SPX timestamp for polling
      const spxData = symbolsData.find(s => s.symbol === "SPX");
      if (spxData && spxData.timestamp) {
        let dbTimestamp;
        if (typeof spxData.timestamp === 'string') {
          dbTimestamp = new Date(spxData.timestamp).toISOString();
        } else if (typeof spxData.timestamp === 'number') {
          dbTimestamp = new Date(spxData.timestamp * 1000).toISOString();
        }
        setDataTimestamps(prev => ({
          ...prev,
          marketIndices: dbTimestamp
        }));
      }
    } catch (error) {
      console.error("Error fetching index data:", error);
    }
  };

  // Fetch index data on component mount
  useEffect(() => {
    // First load existing data quickly
    fetchIndexData(true);
    // Then refresh in background
    setTimeout(() => fetchIndexData(false), 100);
  }, []);

  // Auto-refresh indices every 5 minutes to catch market open/close transitions
  useEffect(() => {
    const intervalId = setInterval(() => {
      console.log("Auto-refreshing index data (5-minute interval)");
      fetchIndexData(false);
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(intervalId);
  }, []);

  // Refresh data when user returns to the tab (catches market status changes while away)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log("Page became visible - checking if refresh needed");
        fetchIndexData(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Function to fetch market movers from database
  const fetchMarketMovers = async (initialLoad = false) => {
    try {
      setLoadingMovers(true);

      console.log("Fetching market movers from database...");

      // Add query parameter to skip update on initial load
      const queryParam = initialLoad ? "?fromDB=true" : "";

      // Fetch both gainers and losers in parallel
      const [gainersResponse, losersResponse] = await Promise.all([
        get({
          apiName: "mktappapi",
          path: `/getMarketMovers/UP${queryParam}`,
        }).response,
        get({
          apiName: "mktappapi",
          path: `/getMarketMovers/DOWN${queryParam}`,
        }).response
      ]);

      const gainersResult = await gainersResponse.body.json();
      const losersResult = await losersResponse.body.json();

      console.log("Gainers result:", gainersResult);
      console.log("Losers result:", losersResult);

      // Extract data arrays from response
      const gainersData = gainersResult.data || [];
      const losersData = losersResult.data || [];

      // Format gainers data
      const gainersWithData = gainersData.map(item => ({
        symbol: item.symbol,
        change: parseFloat(item.percentChange || 0),
        changeDisplay: (parseFloat(item.percentChange || 0) >= 0 ? '+' : '') +
                      parseFloat(item.percentChange || 0).toFixed(2) + '%',
        trend: parseFloat(item.percentChange || 0) >= 0 ? 'up' : 'down',
        exchange: item.exchange || 'NASDAQ'
      }));

      // Format losers data
      const losersWithData = losersData.map(item => ({
        symbol: item.symbol,
        change: parseFloat(item.percentChange || 0),
        changeDisplay: (parseFloat(item.percentChange || 0) >= 0 ? '+' : '') +
                      parseFloat(item.percentChange || 0).toFixed(2) + '%',
        trend: parseFloat(item.percentChange || 0) >= 0 ? 'up' : 'down',
        exchange: item.exchange || 'NASDAQ'
      }));

      console.log("Top 3 gainers:", gainersWithData);
      console.log("Top 3 losers:", losersWithData);

      // Only update state after both datasets are ready
      setTopGainers(gainersWithData);
      setTopLosers(losersWithData);

      // Save timestamp for polling
      if (gainersResult.timestamp) {
        const dbTimestamp = new Date(parseInt(gainersResult.timestamp) * 1000).toISOString();
        setDataTimestamps(prev => ({
          ...prev,
          marketMovers: dbTimestamp
        }));
      }

      // Set updated time to now (when refresh was requested) - only after successful fetch
      // Don't update timestamp on initial load
      if (!initialLoad) {
        const now = new Date();
        const formattedTime = now.toLocaleString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        });
        setMoversUpdatedTime(formattedTime);
      }
    } catch (error) {
      console.error("Error fetching market movers:", error);
      // Don't update state on error - keep showing old data
    } finally {
      setLoadingMovers(false);
    }
  };

  // Fetch market movers on component mount
  useEffect(() => {
    // First load existing data quickly
    fetchMarketMovers(true);
    // Then refresh in background
    setTimeout(() => fetchMarketMovers(false), 100);
  }, []);

  // Function to fetch news from database
  const fetchNews = async (initialLoad = false) => {
    try {
      console.log("Fetching news from database...");

      // Add query parameter to skip update on initial load
      const queryParam = initialLoad ? "?fromDB=true" : "";

      const response = await get({
        apiName: "mktappapi",
        path: `/getNews${queryParam}`,
      }).response;
      const newsArray = await response.body.json();
      console.log("News data:", newsArray);

      // Sort news by time - convert time string to comparable format
      const sortedNews = newsArray.sort((a, b) => {
        // Parse time strings like "Dec 31, 2025, 10:00 AM EST"
        const timeA = new Date(a.time);
        const timeB = new Date(b.time);
        return timeB - timeA; // Most recent first
      });

      setNewsData(sortedNews);

      // Save timestamp for polling
      if (sortedNews.length > 0 && sortedNews[0].timestamp) {
        const dbTimestamp = new Date(parseInt(sortedNews[0].timestamp) * 1000).toISOString();
        setDataTimestamps(prev => ({
          ...prev,
          news: dbTimestamp
        }));
      }

      // Set the updated time to current local time
      // Don't update timestamp on initial load
      if (!initialLoad) {
        const now = new Date();
        const formattedTime = now.toLocaleString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        });
        setNewsUpdatedTime(formattedTime);
      }
    } catch (error) {
      console.error("Error fetching news:", error);
    }
  };

  // Fetch news on component mount
  useEffect(() => {
    // First load existing data quickly
    fetchNews(true);
    // Then refresh in background
    setTimeout(() => fetchNews(false), 100);
  }, []);

  // Function to fetch economic events from database
  const fetchEconomicEvents = async () => {
    try {
      console.log("Fetching economic events...");
      const response = await get({
        apiName: "mktappapi",
        path: "/getEconomicEvents",
      }).response;
      const eventsData = await response.body.json();
      console.log("Economic events data:", eventsData);

      // Get current time
      const now = new Date();

      // Filter events that are happening now or in the future based on date and time strings
      const upcomingEvents = eventsData.filter(event => {
        // Parse the event date/time (format: "2025-12-31 07:00:00")
        if (event.date) {
          const eventDateTime = new Date(event.date);
          return eventDateTime >= now;
        }
        return false;
      });

      console.log(`Current time: ${now}`);
      console.log(`Filtered ${upcomingEvents.length} upcoming events from ${eventsData.length} total events`);

      // Sort by date/time (closest first)
      upcomingEvents.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateA - dateB;
      });

      // Filter out duplicate times, keeping only the first occurrence
      const seenTimes = new Set();
      const uniqueEvents = upcomingEvents.filter(event => {
        if (seenTimes.has(event.time)) {
          return false; // Skip if we've already seen this time
        }
        seenTimes.add(event.time);
        return true;
      });

      console.log("Upcoming events with unique times:", uniqueEvents);
      setEconomicEvents(uniqueEvents);

      // Set the updated time to current local time
      const formattedTime = now.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
      setEventsUpdatedTime(formattedTime);
    } catch (error) {
      console.error("Error fetching economic events:", error);
    }
  };

  // Fetch economic events on component mount
  useEffect(() => {
    fetchEconomicEvents();
  }, []);

  // Function to fetch sector data
  const fetchSectorData = async (initialLoad = false) => {
    try {
      console.log("Fetching sector data...");

      // Step 1: Get current sector data from database
      console.log("Fetching existing sector data from database...");
      const checkResponse = await get({
        apiName: "mktappapi",
        path: "/getEtfSectors",
      }).response;
      const currentSectorsData = await checkResponse.body.json();
      console.log("Current sectors data:", currentSectorsData);

      let sectorsData = currentSectorsData;

      // If this is initial load, just use existing data
      if (initialLoad) {
        console.log("Initial load - using existing data");
      } else {
        // Set updated time to now (when refresh was requested)
        const now = new Date();
        const formattedTime = now.toLocaleString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        });
        setSectorUpdatedTime(formattedTime);

        // Check if data needs to be updated (older than 5 minutes)
        let needsUpdate = true;
        if (currentSectorsData.length > 0 && currentSectorsData[0].timestamp) {
          const dataTimestamp = new Date(currentSectorsData[0].timestamp * 1000);
          const now = new Date();
          const timeDifferenceMinutes = (now - dataTimestamp) / (1000 * 60);

          console.log(`Data age: ${timeDifferenceMinutes.toFixed(2)} minutes`);

          if (timeDifferenceMinutes <= 5) {
            needsUpdate = false;
            console.log("Data is fresh (< 5 minutes old), skipping update");
          } else {
            console.log("Data is stale (> 5 minutes old), updating...");
          }
        } else {
          console.log("No timestamp found or no data, will update");
        }

        // Step 2: Update the sectorEtf table if needed
        if (needsUpdate) {
        console.log("Calling getEtfSectorsPercentages to update table...");
        const updateResponse = await get({
          apiName: "mktappapi",
          path: "/getEtfSectorsPercentages",
        }).response;
        await updateResponse.body.json();
        console.log("Update completed successfully");

        // Brief delay to ensure database is updated
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Step 3: Get updated sector data from database
        console.log("Fetching updated sector data from database...");
        const response = await get({
          apiName: "mktappapi",
          path: "/getEtfSectors",
        }).response;
          sectorsData = await response.body.json();
          console.log("Updated sectors data:", sectorsData);
        }
      }

      // Format sector data
      const formattedSectors = sectorsData.map(sector => {
        const percentChange = parseFloat(sector.percentChange || 0);
        const roundedPercent = percentChange.toFixed(2);
        const displayValue = (percentChange >= 0 ? '+' : '') + roundedPercent + '%';

        return {
          name: sector.sector || sector.etf,
          value: displayValue,
          color: getSectorColor(roundedPercent)
        };
      });

      console.log("Formatted sectors:", formattedSectors);
      setSectorData(formattedSectors);

      // Save timestamp for polling
      if (sectorsData.length > 0 && sectorsData[0].timestamp) {
        const dbTimestamp = new Date(parseInt(sectorsData[0].timestamp) * 1000).toISOString();
        setDataTimestamps(prev => ({
          ...prev,
          sectors: dbTimestamp
        }));
      }
    } catch (error) {
      console.error("Error fetching sector data:", error);
    }
  };

  // Function to check if refresh is needed and update data
  const checkAndRefresh = async () => {
    try {
      // Only check if we have timestamps
      if (!dataTimestamps.marketIndices && !dataTimestamps.marketMovers &&
          !dataTimestamps.news && !dataTimestamps.sectors) {
        console.log("No timestamps available yet, skipping check");
        return;
      }

      console.log("Checking for updates...");
      console.log("Current frontend timestamps:", dataTimestamps);

      const timestampsParam = encodeURIComponent(JSON.stringify(dataTimestamps));
      const response = await get({
        apiName: "mktappapi",
        path: `/checkIfRefresh/${timestampsParam}`,
      }).response;

      const result = await response.body.json();
      console.log("Check result:", result);

      if (result.hasUpdates) {
        console.log("Updates detected, refreshing sections...");

        // Update Market Indices if new data available
        if (result.marketIndices) {
          console.log("Updating market indices with new data");
          const symbolsData = result.marketIndices;

          const updatedIndices = indices.map(index => {
            const symbolData = symbolsData.find(s => s.symbol === index.symbol);
            if (symbolData) {
              const currentPrice = parseFloat(symbolData.regularMarketPrice || 0);
              const dollarChange = parseFloat(symbolData.regularMarketChange || 0);
              const percentChange = parseFloat(symbolData.regularMarketChangePercent || 0);
              const isFuture = symbolData.isFuture || false;

              return {
                ...index,
                value: currentPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                change: (dollarChange >= 0 ? '+' : '') + dollarChange.toFixed(2),
                pct: (percentChange >= 0 ? '+' : '') + percentChange.toFixed(2) + '%',
                trend: dollarChange >= 0 ? 'up' : 'down',
                isFuture: isFuture
              };
            }
            return index;
          });
          setIndices(updatedIndices);

          // Update display timestamp
          const now = new Date();
          const formattedTime = now.toLocaleString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
          });
          setMarketTimestamp(formattedTime);

          // Update timestamp for polling
          const spxData = symbolsData.find(s => s.symbol === "SPX");
          if (spxData && spxData.timestamp) {
            let dbTimestamp;
            if (typeof spxData.timestamp === 'string') {
              dbTimestamp = new Date(spxData.timestamp).toISOString();
            } else if (typeof spxData.timestamp === 'number') {
              dbTimestamp = new Date(spxData.timestamp * 1000).toISOString();
            }
            setDataTimestamps(prev => ({ ...prev, marketIndices: dbTimestamp }));
          }
        }

        // Update Market Movers if new data available
        if (result.marketMovers) {
          console.log("Updating market movers with new data");
          const gainersData = result.marketMovers.gainers;
          const losersData = result.marketMovers.losers;

          const gainersWithData = gainersData.map(item => ({
            symbol: item.symbol,
            change: parseFloat(item.percentChange || 0),
            changeDisplay: (parseFloat(item.percentChange || 0) >= 0 ? '+' : '') +
                          parseFloat(item.percentChange || 0).toFixed(2) + '%',
            trend: parseFloat(item.percentChange || 0) >= 0 ? 'up' : 'down',
            exchange: item.exchange || 'NASDAQ'
          }));

          const losersWithData = losersData.map(item => ({
            symbol: item.symbol,
            change: parseFloat(item.percentChange || 0),
            changeDisplay: (parseFloat(item.percentChange || 0) >= 0 ? '+' : '') +
                          parseFloat(item.percentChange || 0).toFixed(2) + '%',
            trend: parseFloat(item.percentChange || 0) >= 0 ? 'up' : 'down',
            exchange: item.exchange || 'NASDAQ'
          }));

          setTopGainers(gainersWithData);
          setTopLosers(losersWithData);

          // Update display timestamp
          const now = new Date();
          const formattedTime = now.toLocaleString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
          });
          setMoversUpdatedTime(formattedTime);

          // Update timestamp for polling
          if (result.marketMovers.timestamp) {
            const dbTimestamp = new Date(parseInt(result.marketMovers.timestamp) * 1000).toISOString();
            setDataTimestamps(prev => ({ ...prev, marketMovers: dbTimestamp }));
          }
        }

        // Update News if new data available
        if (result.news) {
          console.log("Updating news with new data");
          const sortedNews = result.news.sort((a, b) => {
            const timeA = new Date(a.time);
            const timeB = new Date(b.time);
            return timeB - timeA;
          });
          setNewsData(sortedNews);

          // Update display timestamp
          const now = new Date();
          const formattedTime = now.toLocaleString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
          });
          setNewsUpdatedTime(formattedTime);

          // Update timestamp for polling
          if (sortedNews.length > 0 && sortedNews[0].timestamp) {
            const dbTimestamp = new Date(parseInt(sortedNews[0].timestamp) * 1000).toISOString();
            setDataTimestamps(prev => ({ ...prev, news: dbTimestamp }));
          }
        }

        // Update Sectors if new data available
        if (result.sectors) {
          console.log("Updating sectors with new data");
          const formattedSectors = result.sectors.map(sector => {
            const percentChange = parseFloat(sector.percentChange || 0);
            const roundedPercent = percentChange.toFixed(2);
            const displayValue = (percentChange >= 0 ? '+' : '') + roundedPercent + '%';

            return {
              name: sector.sector || sector.etf,
              value: displayValue,
              color: getSectorColor(roundedPercent)
            };
          });
          setSectorData(formattedSectors);

          // Update display timestamp
          const now = new Date();
          const formattedTime = now.toLocaleString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
          });
          setSectorUpdatedTime(formattedTime);

          // Update timestamp for polling
          if (result.sectors.length > 0 && result.sectors[0].timestamp) {
            const dbTimestamp = new Date(parseInt(result.sectors[0].timestamp) * 1000).toISOString();
            setDataTimestamps(prev => ({ ...prev, sectors: dbTimestamp }));
          }
        }
      } else {
        console.log("No updates needed");
      }
    } catch (error) {
      console.error("Error checking for refresh:", error);
    }
  };

  // Set up polling every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      checkAndRefresh();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [dataTimestamps, indices]); // Re-create interval when timestamps change

  // Function to fetch market impact watchlist
  const fetchMarketImpactWatchlist = async (initialLoad = false) => {
    try {
      console.log("Fetching market impact...");

      // Step 1: Get watchlist securities from database
      const watchlistResponse = await get({
        apiName: "mktappapi",
        path: "/getMarketImpactWatchlist",
      }).response;
      const watchlistData = await watchlistResponse.body.json();
      console.log("Watchlist data:", watchlistData);

      if (watchlistData && watchlistData.length > 0) {
        // Step 2: Extract all securities
        const securities = watchlistData.map(item => item.security);
        console.log("Securities list:", securities);

        // Step 3: Call getSecuritiesInfo with all securities in one request
        const securitiesParam = JSON.stringify({ stocks: securities });
        const getSecuritiesInfoPath = `/getSecuritiesInfo/${encodeData(securitiesParam)}`;

        console.log("Calling getSecuritiesInfo for market impact securities...");
        const securitiesInfoResponse = await get({
          apiName: "mktappapi",
          path: getSecuritiesInfoPath,
        }).response;
        await securitiesInfoResponse.body.json();

        // Brief delay to ensure database is updated
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Step 4: Get updated symbols details from database
        const symbolsParam = securities.join(',');
        const symbolsDetailsResponse = await get({
          apiName: "mktappapi",
          path: `/getSymbolsDetails/${encodeURIComponent(symbolsParam)}`,
        }).response;
        const symbolsDetails = await symbolsDetailsResponse.body.json();
        console.log("Symbols details:", symbolsDetails);

        // Step 5: Merge watchlist data with securities info
        const mergedData = watchlistData.map(watchlistItem => {
          const securityInfo = symbolsDetails.find(s => s.symbol === watchlistItem.security);
          return {
            security: watchlistItem.security,
            fullName: watchlistItem.fullName,
            sector: watchlistItem.sector,
            lastSale: securityInfo ? parseFloat(securityInfo.regularMarketPrice || 0).toFixed(2) : 'N/A',
            change: securityInfo ? parseFloat(securityInfo.regularMarketChange || 0).toFixed(2) : '0.00',
            changePercent: securityInfo ? parseFloat(securityInfo.regularMarketChangePercent || 0).toFixed(2) : '0.00',
            earningsTime: securityInfo && securityInfo.earningsTimestampStart ?
              new Date(securityInfo.earningsTimestampStart * 1000).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              }) : 'N/A'
          };
        });

        // Group by sector
        const groupedBySector = mergedData.reduce((acc, item) => {
          if (!acc[item.sector]) {
            acc[item.sector] = [];
          }
          acc[item.sector].push(item);
          return acc;
        }, {});

        // Sort sectors: Information Technology first, Financials second, then rest alphabetically
        const sortedGroupedBySector = {};
        const sectorOrder = ['Information Technology', 'Financials'];

        // Add priority sectors first
        sectorOrder.forEach(sector => {
          if (groupedBySector[sector]) {
            sortedGroupedBySector[sector] = groupedBySector[sector];
          }
        });

        // Add remaining sectors alphabetically
        Object.keys(groupedBySector)
          .filter(sector => !sectorOrder.includes(sector))
          .sort()
          .forEach(sector => {
            sortedGroupedBySector[sector] = groupedBySector[sector];
          });

        console.log("Grouped data:", sortedGroupedBySector);
        setMarketImpactData(sortedGroupedBySector);

        // Set updated time
        if (!initialLoad) {
          const now = new Date();
          const formattedTime = now.toLocaleString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
          });
          setMarketImpactUpdatedTime(formattedTime);
        }
      }
    } catch (error) {
      console.error("Error fetching market impact:", error);
    }
  };

  // Fetch volatility data
  const fetchVolatilityData = async (initialLoad = false) => {
    try {
      console.log("=== STARTING fetchVolatilityData ===");
      console.log("Fetching volatility data...");

      // Step 1: Get volatility list from database
      const volatilityResponse = await get({
        apiName: "mktappapi",
        path: "/getVolatilityList",
      }).response;
      const volatilityList = await volatilityResponse.body.json();
      console.log("Volatility data from database:", volatilityList);

      if (volatilityList && volatilityList.length > 0) {
        console.log("Volatility list has", volatilityList.length, "items");

        // Step 2: Extract security symbols (standard names like VIX, RVX, etc.)
        const securities = volatilityList.map(item => item.security);
        console.log("Volatility securities list:", securities);
        console.log("About to call getSecuritiesInfo with security symbols...");

        // Step 3: Call getSecuritiesInfo with security symbols to fetch fresh data
        const securitiesParam = JSON.stringify({ stocks: securities });
        const getSecuritiesInfoPath = `/getSecuritiesInfo/${encodeData(securitiesParam)}`;

        console.log("Calling getSecuritiesInfo for volatility symbols...");
        console.log("Path:", getSecuritiesInfoPath);
        const securitiesInfoResponse = await get({
          apiName: "mktappapi",
          path: getSecuritiesInfoPath,
        }).response;
        const securitiesInfoResult = await securitiesInfoResponse.body.json();
        console.log("getSecuritiesInfo result:", securitiesInfoResult);

        // Brief delay to ensure database is updated
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Step 4: Get updated symbols details from database using security symbols
        const symbolsParam = securities.join(',');
        console.log("Querying getSymbolsDetails with symbols:", symbolsParam);
        const symbolsDetailsResponse = await get({
          apiName: "mktappapi",
          path: `/getSymbolsDetails/${encodeURIComponent(symbolsParam)}`,
        }).response;
        const symbolsDetails = await symbolsDetailsResponse.body.json();
        console.log("Volatility symbols details from DB:", symbolsDetails);
        console.log("Number of symbols returned:", symbolsDetails.length);

        // Step 5: Merge volatility data with securities info
        const mergedData = volatilityList.map(volatilityItem => {
          const securityInfo = symbolsDetails.find(s => s.symbol === volatilityItem.security);
          return {
            security: volatilityItem.security,
            shortName: volatilityItem.shortName,
            lastSale: securityInfo ? parseFloat(securityInfo.regularMarketPrice || 0).toFixed(2) : 'N/A',
            change: securityInfo ? parseFloat(securityInfo.regularMarketChange || 0).toFixed(2) : '0.00',
            changePercent: securityInfo ? parseFloat(securityInfo.regularMarketChangePercent || 0).toFixed(2) : '0.00'
          };
        });

        // Sort volatility data in specific order
        const sortOrder = ['VIX1D', 'VIX9D', 'VIX', 'VIX3M', 'VIX6M', 'VIX1Y', 'RVX', 'VXN'];
        const sortedMergedData = mergedData.sort((a, b) => {
          const indexA = sortOrder.indexOf(a.security.toUpperCase());
          const indexB = sortOrder.indexOf(b.security.toUpperCase());
          // If not in sort order, put at end
          if (indexA === -1 && indexB === -1) return 0;
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        });

        console.log("Merged and sorted volatility data:", sortedMergedData);
        setVolatilityData(sortedMergedData);

        // Set updated time
        if (!initialLoad) {
          const now = new Date();
          const formattedTime = now.toLocaleString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
          });
          setVolatilityUpdatedTime(formattedTime);
        }
      }
    } catch (error) {
      console.error("=== ERROR in fetchVolatilityData ===");
      console.error("Error fetching volatility data:", error);
      console.error("Error stack:", error.stack);
    }
  };

  // Function to refresh all sections
  const refreshAllSections = async () => {
    try {
      setRefreshingAll(true);
      console.log("Refreshing all sections...");

      // Call all refresh functions in parallel
      await Promise.all([
        fetchIndexData(),
        fetchVolatilityData(),
        fetchMarketMovers(),
        fetchNews(),
        fetchEconomicEvents(),
        fetchSectorData(),
        fetchMarketImpactWatchlist()
      ]);

      console.log("All sections refreshed successfully");
    } catch (error) {
      console.error("Error refreshing all sections:", error);
    } finally {
      setRefreshingAll(false);
    }
  };

  // Fetch sector data on component mount
  useEffect(() => {
    // First load existing data quickly
    fetchSectorData(true);
    // Then refresh in background
    setTimeout(() => fetchSectorData(false), 100);
  }, []);

  // Fetch market impact watchlist on component mount
  useEffect(() => {
    // First load existing data quickly
    fetchMarketImpactWatchlist(true);
    // Then refresh in background
    setTimeout(() => fetchMarketImpactWatchlist(false), 100);
  }, []);

  // Fetch volatility data on component mount
  useEffect(() => {
    console.log("=== Volatility useEffect running ===");
    // First load existing data quickly
    fetchVolatilityData(true);
    // Then refresh in background
    setTimeout(() => fetchVolatilityData(false), 100);
  }, []);

  return (
    <AppLayout signOut={signOut} onRefresh={refreshAllSections} refreshing={refreshingAll}>
      {/* Market Indices */}
          <div style={{ marginBottom: '24px' }}>
            <div className="landing-panel-header" style={{ marginBottom: '16px' }}>
              <h2 className="landing-panel-title">Market Indices</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'rgba(0, 0, 0, 0.7)' }}>
                <span>Updated: {marketTimestamp || "Loading..."}</span>
                <button
                  onClick={fetchIndexData}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    color: 'rgba(0, 0, 0, 0.7)',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#3b82f6'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(0, 0, 0, 0.7)'}
                  title="Refresh market indices"
                >
                  <RefreshCw size={14} />
                </button>
              </div>
            </div>
            <div className="landing-indices-grid">
              {indices.map((index) => (
                <div key={index.name} className={`landing-index-card ${index.isFuture ? 'future' : ''}`}>
                  <div className="landing-index-header">
                    <span className="landing-index-name">
                      {index.name}
                      {index.isFuture && <span className="landing-futures-badge">Futures</span>}
                    </span>
                    <span className={`landing-index-badge ${index.trend === 'up' ? 'positive' : 'negative'}`}>
                      {index.pct}
                    </span>
                  </div>
                  <div className="landing-index-value">{index.value}</div>
                  <div className={`landing-index-change ${index.trend === 'up' ? 'positive' : 'negative'}`}>
                    {index.trend === 'up' ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                    <span>{index.change}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Market Volatility */}
          <div style={{ marginBottom: '24px' }}>
            <div className="landing-panel-header" style={{ marginBottom: '16px' }}>
              <h2 className="landing-panel-title">Market Volatility</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'rgba(0, 0, 0, 0.7)' }}>
                <span>Updated: {volatilityUpdatedTime || "Loading..."}</span>
                <button
                  onClick={fetchVolatilityData}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    color: 'rgba(0, 0, 0, 0.7)',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#3b82f6'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(0, 0, 0, 0.7)'}
                  title="Refresh volatility"
                >
                  <RefreshCw size={14} />
                </button>
              </div>
            </div>
            <div className="landing-indices-grid">
              {volatilityData.map((item) => {
                const isLoading = item.lastSale === "...";
                const changeNum = parseFloat(item.change);
                const changePositive = !isLoading && changeNum >= 0;
                return (
                <div key={item.security} className="landing-index-card">
                  <div className="landing-index-header">
                    <span className="landing-index-name">
                      <a
                        href={`https://www.google.com/finance/quote/${item.security}:INDEXCBOE?window=1Y`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'inherit', textDecoration: 'none', display: 'flex', flexDirection: 'column', gap: '2px' }}
                      >
                        <span>{item.security}</span>
                        <span style={{ fontSize: '11px', opacity: 0.7, fontWeight: 'normal' }}>{item.shortName}</span>
                      </a>
                    </span>
                    <span className={`landing-index-badge ${isLoading ? '' : (changePositive ? 'positive' : 'negative')}`}>
                      {isLoading ? '...' : `${changePositive ? '+' : ''}${item.changePercent}%`}
                    </span>
                  </div>
                  <div className="landing-index-value">{item.lastSale}</div>
                  <div className={`landing-index-change ${isLoading ? '' : (changePositive ? 'positive' : 'negative')}`}>
                    {!isLoading && (changePositive ? <TrendingUp size={16} /> : <TrendingDown size={16} />)}
                    <span>{isLoading ? '...' : `${changePositive ? '+' : ''}${item.change}`}</span>
                  </div>
                </div>
                );
              })}
            </div>
          </div>

          <div className="landing-content-grid">
            {/* Left Column - News & Calendar */}
            <div>
              {/* News Section */}
              <div className="landing-panel">
                <div className="landing-panel-header">
                  <h2 className="landing-panel-title">News</h2>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'rgba(0, 0, 0, 0.7)' }}>
                    <span>Updated: {newsUpdatedTime || "Loading..."}</span>
                    <button
                      onClick={fetchNews}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        color: 'rgba(0, 0, 0, 0.7)',
                        transition: 'color 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#3b82f6'}
                      onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(0, 0, 0, 0.7)'}
                      title="Refresh news"
                    >
                      <RefreshCw size={14} />
                    </button>
                  </div>
                </div>

                <div className="landing-panel-content">
                  <div className="landing-news-tabs">
                    {["Business", "Investing", "TV Networks", "Tech", "Tabloid", "Other"].map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setActiveNewsTab(tab)}
                        className={`landing-news-tab ${activeNewsTab === tab ? 'active' : ''}`}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>

                  <div className="landing-news-list" style={{
                    marginTop: '16px',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    paddingRight: '4px'
                  }}>
                    {newsData.length > 0 ? (
                      categorizeNews(newsData)[activeNewsTab]?.map((item, idx) => (
                        <div key={idx} className="landing-news-item">
                          <div className="landing-news-content">
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="landing-news-title"
                              style={{
                                color: 'rgba(0, 0, 0, 0.9)',
                                textDecoration: 'none',
                                cursor: 'pointer',
                              }}
                              onMouseEnter={(e) => e.target.style.color = '#3b82f6'}
                              onMouseLeave={(e) => e.target.style.color = 'rgba(0, 0, 0, 0.9)'}
                            >
                              {item.title}
                            </a>
                            <p style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.5)', marginTop: '4px' }}>
                              {item.source}
                            </p>
                          </div>
                          <span className="landing-news-time">{item.time}</span>
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: '16px', textAlign: 'center', color: 'rgba(0, 0, 0, 0.5)' }}>
                        Loading news...
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Economic Calendar */}
              <div className="landing-panel">
                <div className="landing-panel-header">
                  <h2 className="landing-panel-title">Economic Calendar</h2>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'rgba(0, 0, 0, 0.7)' }}>
                    <span>Updated: {eventsUpdatedTime || "Loading..."}</span>
                    <button
                      onClick={fetchEconomicEvents}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        color: 'rgba(0, 0, 0, 0.7)',
                        transition: 'color 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#3b82f6'}
                      onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(0, 0, 0, 0.7)'}
                      title="Refresh events"
                    >
                      <RefreshCw size={14} />
                    </button>
                  </div>
                </div>
                <div className="landing-panel-content" style={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  paddingRight: '4px'
                }}>
                  {economicEvents.length > 0 ? (
                    economicEvents.map((event, idx) => (
                      <div key={idx} className="landing-event">
                        <div className="landing-event-details">
                          <span className="landing-event-time">{event.date ? event.date.split(" ")[0] : ''} {event.time}</span>
                          <span className="landing-event-name">{event.report}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '16px', textAlign: 'center', color: 'rgba(0, 0, 0, 0.5)' }}>
                      No events for today
                    </div>
                  )}
                </div>
              </div>

              {/* Sector Heatmap */}
              <div className="landing-panel">
                <div className="landing-panel-header">
                  <h2 className="landing-panel-title">Sector Heatmap</h2>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'rgba(0, 0, 0, 0.7)' }}>
                    <span>Updated: {sectorUpdatedTime || "Loading..."}</span>
                    <button
                      onClick={fetchSectorData}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        color: 'rgba(0, 0, 0, 0.7)',
                        transition: 'color 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#3b82f6'}
                      onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(0, 0, 0, 0.7)'}
                      title="Refresh sector data"
                    >
                      <RefreshCw size={14} />
                    </button>
                  </div>
                </div>
                <div className="landing-panel-content">
                  <div className="landing-heatmap-grid">
                    {sectorData.length > 0 ? (
                      sectorData.map((sector) => (
                        <div
                          key={sector.name}
                          className="landing-heatmap-cell"
                          style={{ backgroundColor: sector.color }}
                        >
                          <div className="landing-heatmap-name">{sector.name}</div>
                          <div className="landing-heatmap-value">{sector.value}</div>
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: '16px', textAlign: 'center', color: 'rgba(0, 0, 0, 0.5)' }}>
                        Loading sector data...
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Sidebar - Global Markets, Watchlist, Quick Actions */}
            <div>
              {/* Market Impact Watchlist */}
              <div className="landing-panel">
                <div className="landing-panel-header">
                  <h2 className="landing-panel-title">Market Impact</h2>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'rgba(0, 0, 0, 0.7)' }}>
                    <span>Updated: {marketImpactUpdatedTime || "Loading..."}</span>
                    <button
                      onClick={fetchMarketImpactWatchlist}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        color: 'rgba(0, 0, 0, 0.7)',
                        transition: 'color 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#3b82f6'}
                      onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(0, 0, 0, 0.7)'}
                      title="Refresh market impact"
                    >
                      <RefreshCw size={14} />
                    </button>
                  </div>
                </div>
                <div className="landing-panel-content" style={{
                  maxHeight: '400px',
                  overflowY: 'auto',
                  paddingRight: '4px'
                }}>
                  {Object.keys(marketImpactData).length > 0 ? (
                    Object.keys(marketImpactData).map((sector) => (
                      <div key={sector} style={{ marginBottom: '16px' }}>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#000000',
                          marginBottom: '8px',
                          paddingBottom: '4px',
                          borderBottom: '1px solid #e5e7eb'
                        }}>
                          {sector}
                        </div>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(2, 1fr)',
                          gap: '8px'
                        }}>
                          {marketImpactData[sector].map((stock) => (
                            <div
                              key={stock.security}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '8px',
                                backgroundColor: '#f9fafb',
                                borderRadius: '4px'
                              }}
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <a
                                  href={`https://www.google.com/finance/quote/${stock.security}:NASDAQ?window=1Y`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: '#3b82f6',
                                    textDecoration: 'none',
                                    fontWeight: '600',
                                    fontSize: '14px'
                                  }}
                                >
                                  {stock.security}
                                </a>
                                <div style={{
                                  fontSize: '10px',
                                  color: 'rgba(0, 0, 0, 0.5)',
                                  marginTop: '2px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {stock.fullName}
                                </div>
                              </div>
                              <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end',
                                gap: '2px',
                                marginLeft: '8px'
                              }}>
                                <div style={{
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  color: '#000000'
                                }}>
                                  ${stock.lastSale}
                                </div>
                                <div style={{
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  color: parseFloat(stock.change) < 0 ? '#ef4444' : '#10b981'
                                }}>
                                  {parseFloat(stock.change) < 0 ? '' : '+'}{stock.change} ({parseFloat(stock.changePercent) < 0 ? '' : '+'}{stock.changePercent}%)
                                </div>
                                <div style={{
                                  fontSize: '10px',
                                  color: 'rgba(0, 0, 0, 0.5)'
                                }}>
                                  Earn: {stock.earningsTime}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '16px', textAlign: 'center', color: 'rgba(0, 0, 0, 0.5)' }}>
                      Loading market impact...
                    </div>
                  )}
                </div>
              </div>

              {/* Market Movers */}
              <div className="landing-panel">
                <div className="landing-panel-header">
                  <h2 className="landing-panel-title">Market Top Gainers & Losers</h2>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'rgba(0, 0, 0, 0.7)' }}>
                    <span>
                      {loadingMovers ? "Loading data..." : `Updated: ${moversUpdatedTime || "Loading..."}`}
                    </span>
                    <button
                      onClick={fetchMarketMovers}
                      disabled={loadingMovers}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: loadingMovers ? 'not-allowed' : 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        color: 'rgba(0, 0, 0, 0.7)',
                        transition: 'color 0.2s',
                        opacity: loadingMovers ? 0.5 : 1
                      }}
                      onMouseEnter={(e) => !loadingMovers && (e.currentTarget.style.color = '#3b82f6')}
                      onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(0, 0, 0, 0.7)'}
                      title="Refresh market movers"
                    >
                      <RefreshCw size={14} className={loadingMovers ? 'spinning' : ''} />
                    </button>
                  </div>
                </div>
                <div className="landing-panel-content" style={{ padding: '8px' }}>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', padding: '0 8px' }}>
                    <button
                      className={`landing-news-tab ${activeMarketTab === 'gainers' ? 'active' : ''}`}
                      onClick={() => setActiveMarketTab('gainers')}
                    >
                      Top Gainers
                    </button>
                    <button
                      className={`landing-news-tab ${activeMarketTab === 'losers' ? 'active' : ''}`}
                      onClick={() => setActiveMarketTab('losers')}
                    >
                      Top Losers
                    </button>
                  </div>
                  <div className="landing-market-list">
                    {(activeMarketTab === 'gainers' ? topGainers : topLosers).map((stock) => (
                      <div key={stock.symbol} className="landing-market-item">
                        <a
                          href={`https://www.google.com/finance/quote/${stock.symbol}:${stock.exchange || 'NASDAQ'}?window=1Y`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="landing-market-name"
                          style={{ color: '#3b82f6', textDecoration: 'none' }}
                        >
                          {stock.symbol}
                        </a>
                        <span className={`landing-market-value ${stock.trend === 'up' ? 'positive' : 'negative'}`}
                              style={{ color: stock.trend === 'up' ? '#10b981' : '#ef4444' }}>
                          {stock.changeDisplay}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="landing-panel">
                <div className="landing-panel-header">
                  <h2 className="landing-panel-title">Quick Actions</h2>
                </div>
                <div className="landing-panel-content">
                  <div className="landing-actions-grid">
                    {quickActions.map((action) => (
                      <div key={action.label}>
                        {action.hasSubmenu ? (
                          <>
                            <button
                              onClick={() => action.setExpanded(!action.expanded)}
                              className="landing-action-button"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                              }}
                            >
                              <span>{action.label}</span>
                              <ChevronDown
                                size={14}
                                style={{
                                  transition: 'transform 0.2s',
                                  transform: action.expanded ? 'rotate(180deg)' : 'rotate(0deg)'
                                }}
                              />
                            </button>
                            {action.expanded && (
                              <div style={{ marginLeft: '16px', marginTop: '4px' }}>
                                {action.submenu.map((subItem) => (
                                  <button
                                    key={subItem.label}
                                    onClick={() => {
                                      if (!subItem.disabled) {
                                        if (subItem.state) {
                                          navigate(subItem.route, { state: subItem.state });
                                        } else {
                                          navigate(subItem.route);
                                        }
                                      }
                                    }}
                                    className="landing-action-button"
                                    style={{
                                      fontSize: '13px',
                                      padding: '8px 12px',
                                      marginBottom: '4px',
                                      opacity: subItem.disabled ? 0.5 : 1,
                                      cursor: subItem.disabled ? 'not-allowed' : 'pointer',
                                      pointerEvents: subItem.disabled ? 'none' : 'auto'
                                    }}
                                  >
                                    {subItem.label}
                                  </button>
                                ))}
                              </div>
                            )}
                          </>
                        ) : (
                          <button
                            onClick={() => navigate(action.route)}
                            className="landing-action-button"
                          >
                            {action.label}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
    </AppLayout>
  );
}
